/**
 * @typedef {{valuations: Valuation[]; errorMessage?: string; isGated?: boolean}} Props
 * @typedef {import('../../../../shared/AgentService/ValuationData').ValuationResponse} Valuation
 * @typedef {import('./TableData').default} TableData
 */

import { Link } from 'react-router-dom';
import { toAbsoluteUrl } from '../../../../../utils/toAbsoluteUrl';
import { translate } from '../../../../Internationalization';
import BaseTable from '../../../../../shared/BaseTable';
import ExternalAnchor from '../../../../../shared/ExternalAnchor';
import getValuationTableData from './valuationTableData';
import images from '../../../../../config/local/images';
import LeadScore from '../../../../shared/LeadScore/LeadScore';
import paths from '../../../../../config/local/paths';
import React, { useCallback, useMemo } from 'react';
import useAuth from '../../../../../Hooks/Shared/useAuth/useAuth';
import valuationTableHeader from './valuationTableHeader';

const NOT_APPLICABLE = 'N/A';

/**
 * @param {Props} props
 * @returns {JSX.Element}
 */
function ValuationsTable(props) {
	const { agentSubscriptionLevels } = useAuth();
	const isSubscribed = useMemo(
		() =>
			agentSubscriptionLevels.includes('agent') ||
			agentSubscriptionLevels.includes('group') ||
			agentSubscriptionLevels.includes('domain'),
		[agentSubscriptionLevels]
	);

	/**
	 * @param {string} header
	 * @returns {string | JSX.Element}
	 */
	function moverScoreHeader(header) {
		if (isSubscribed) {
			return header;
		}

		return (
			<div className="flex items-center">
				<Link
					className="w-3.5 mr-1"
					title="Click here to upgrade"
					to={paths.app.agent.subscription.root}
				>
					<img
						alt="Feature has been locked and requires a subscription"
						src={toAbsoluteUrl(images.icons.lock)}
					/>
				</Link>
				{header}
			</div>
		);
	}

	/**
	 * @param {Valuation} valuation
	 * @returns {TableData}
	 */
	function name(valuation) {
		if (!valuation.contact?.name) {
			return NOT_APPLICABLE;
		}

		if (props.isGated) {
			return valuation.contact?.name;
		}

		return (
			<Link
				className="hover-color-primary no-underline text-black"
				to={{
					pathname: `${paths.app.agent.contacts.edit}/${valuation.contact.id}`,
				}}
			>
				{valuation.contact?.name}
			</Link>
		);
	}

	/**
	 * @param {Valuation['contact']} contact
	 * @param {['email', 'mailto'] | ['phone', 'tel']} fields
	 * @returns {TableData}
	 */
	function externalLink(contact, fields) {
		if (!contact?.[fields[0]]) {
			return NOT_APPLICABLE;
		}

		if (props.isGated) {
			return contact[fields[0]];
		}

		return (
			<ExternalAnchor
				className="hover-color-primary no-underline text-black"
				href={`${fields[1]}:${contact[fields[0]]}`}
			>
				{contact[fields[0]]}
			</ExternalAnchor>
		);
	}

	/**
	 * @param {Valuation} valuation
	 * @returns {JSX.Element | string}
	 */
	function moverScore(valuation) {
		const { contact } = valuation ?? {};

		if (!contact) {
			return NOT_APPLICABLE;
		}

		const subscribed = isSubscribed || contact.hasGroupSubscription;

		if (subscribed) {
			return (
				<LeadScore
					leadScore={contact.leadScore}
					leadScores={contact.leadScores}
				/>
			);
		}

		return (
			<Link
				className="no-underline flex items-center hover-lighter text-info"
				to={paths.app.agent.subscription.root}
			>
				{translate('agent.pages.contacts.view.score')}
			</Link>
		);
	}

	const tableHeader = useCallback(() => valuationTableHeader(moverScoreHeader),[]); // prettier-ignore

	const tableData = useCallback(
		() =>
			getValuationTableData(props.valuations, {
				email: ({contact}) =>	externalLink(contact, ['email', 'mailto']), // prettier-ignore
				moverScore,
				name,
				phone: ({contact}) => externalLink(contact, ['phone', 'tel']), // prettier-ignore
			}),
		[props.valuations]
	);

	return (
		<BaseTable
			bodyData={tableData()}
			errorMessage={props.errorMessage}
			headerData={tableHeader()}
		/>
	);
}

export default React.memo(ValuationsTable);
